import { formatRupeeLabel } from '../../../../display-values/lumpsumPlanAmount'
import { type ReactNode, useId } from 'react'
import { getScoreConfig } from '../../../../../../../components/know-why/utils'
import { NumberIcon } from '../../../../../../../components/ui-components/NumberIcon'
import RenderHTML from '../../../../../../../components/ui-components/RenderHTML'
import { EliteScoreTrends } from '../../../../../../../types/mutual-fund-detail.types'
import { buildPlanAllocationRangeLabel, planPercentageLabel } from '../../../../display-values/lumpsumPlanPercentage'
import type { FundMissedGainsInfoParams } from '../missed-gains/FundMissedGainsInfoBottomSheet'
import type { PlanRecommendationPairView } from '../../pair/planRecommendationPair.types'
import { isMissedGainsComparisonAvailable } from '../../../plans-list/header/rebalance/MissedGainsComparisonBarGraph'
import { EventNames } from '../../../../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../../../../utils/appBridge'
import { PlansLumpsumSheetEventIds, PlansScreen } from '../../../../analytics/plansEvents'
import {
  PlanRecommendationReasonGradientStrip,
  resolveRankStripTagFromPowerRating
} from './PlanRecommendationReasonStrip'

export type ReasonPointerData = {
  pair: PlanRecommendationPairView
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
}

type ReasonPointerWrapperProps = {
  index?: number
  badgeLabel?: string | number
  data: ReasonPointerData
  analyticsScreen?: string
}

export function BetterReturnsPointer({
  data,
  index,
  badgeLabel,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation
}: ReasonPointerWrapperProps) {
  const xirrDiff = data.pair.underPerformance?.xirrDiff ?? 0
  const xirrDiffDurationInMonths = data.pair.underPerformance?.xirrDiffDurationInMonths ?? 0

  return (
    <ReasonPointer
      index={index}
      badgeLabel={badgeLabel}
      title="Better returns"
      subtitle="than your current fund"
      highlightTitle={`+ ${planPercentageLabel({ rawPercentage: xirrDiff })} XIRR`}
      highlightDescription={`Higher returns in the last ${xirrDiffDurationInMonths} months, and is expected to maintain this performance`}
      tone="positive"
    />
  )
}

export function ExcellentRankPointer({
  data,
  index,
  badgeLabel
}: ReasonPointerWrapperProps) {
  return (
    <ReasonPointer
      index={index}
      badgeLabel={badgeLabel}
      title="Excellent Power Rank"
      subtitle="in its category"
      highlightContent={(
        <ReasonPointerRankHighlightStrip
          powerRank={data.pair.enteringFund.powerRank}
          powerRating={data.pair.enteringFund.powerRating}
          description="Top ranked, outperforming the benchmark and your current fund"
          tone="positive"
        />
      )}
    />
  )
}

export function MissedGainsPointer({
  data,
  index,
  badgeLabel
}: ReasonPointerWrapperProps) {
  // Copy + missed-gains sheet use the held fund being left (pair exiting side).
  const currentFund = data.pair.exitingFund
  const missedGains = data.pair.underPerformance?.missedGains
  const currentValue = currentFund?.currentValue
  // Pair maps missing currentValue → 0; only show info when the comparison sheet can render.
  const canOpenMissedGainsInfo = isMissedGainsComparisonAvailable({
    missedGains,
    baselineValue: currentValue
  })

  return (
    <ReasonPointer
      index={index}
      badgeLabel={badgeLabel}
      title="Missed gains"
      subtitle="on your current fund"
      highlightContent={(
        <ReasonPointerHighlightStrip
          highlightTitle={`+ ${formatRupeeLabel({ rawAmount: missedGains ?? 0 })}`}
          highlightDescription="Returns you missed by staying in your current fund instead of switching earlier"
          tone="warning"
          fundName={currentFund?.fundName ?? ''}
          fundIsin={currentFund?.isin ?? ''}
          onInfoClick={
            canOpenMissedGainsInfo
              ? () => {
                  data.onMissedGainsInfoClick({
                    missedGains: missedGains ?? 0,
                    currentValue: currentValue ?? 0,
                    planSnapshotDate: data.pair.planSnapshotDate
                  })
                }
              : undefined
          }
        />
      )}
    />
  )
}

export function ReduceExposurePointer({
  data,
  index,
  badgeLabel
}: ReasonPointerWrapperProps) {
  const categoryName = data.pair.exitingFund.categoryName
  const idealAllocationRangeText = buildPlanAllocationRangeLabel({ idealAllocation: {
    lowerLimit: data.pair.overExposure?.sellFundIdealAllocationPercentage?.lowerLimit ?? 0,
    upperLimit: data.pair.overExposure?.sellFundIdealAllocationPercentage?.upperLimit ?? 0
  } })

  return (
    <ReasonPointer
      index={index}
      badgeLabel={badgeLabel}
      title={`Helps reduce ${categoryName} exposure`}
      subtitle={`to ideal range of ${idealAllocationRangeText}`}
      highlightContent={(
        <ReasonPointerHighlightStrip
          highlightTitle={`- ${planPercentageLabel({ rawPercentage: data.pair.overExposure?.reduceExposurePercentage ?? 0 })}`}
          highlightDescriptionContent={(
            <ReasonPointerCategoryExposureDescription
              categoryName={categoryName}
              actionDescription={`by fully selling ${data.pair.exitingFund.fundName}`}
            />
          )}
          tone="positive"
        />
      )}
    />
  )
}

export function IncreaseExposurePointer({
  data,
  index,
  badgeLabel
}: ReasonPointerWrapperProps) {
  const categoryName = data.pair.enteringFund.categoryName
  const idealAllocationRangeText = buildPlanAllocationRangeLabel({ idealAllocation: {
    lowerLimit: data.pair.overExposure?.buyFundIdealAllocationPercentage?.lowerLimit ?? 0,
    upperLimit: data.pair.overExposure?.buyFundIdealAllocationPercentage?.upperLimit ?? 0
  } })

  return (
    <ReasonPointer
      index={index}
      badgeLabel={badgeLabel}
      title={`Helps increase ${categoryName} exposure`}
      subtitle={`to ideal range of ${idealAllocationRangeText}`}
      highlightContent={(
        <ReasonPointerHighlightStrip
          highlightTitle={`+ ${planPercentageLabel({ rawPercentage: data.pair.overExposure?.reduceExposurePercentage ?? 0 })}`}
          highlightDescriptionContent={(
            <ReasonPointerCategoryExposureDescription
              categoryName={categoryName}
              actionDescription={`by buying ${data.pair.enteringFund.fundName}`}
            />
          )}
          tone="positive"
        />
      )}
    />
  )
}

export function ReduceOverDiversificationPointer({
  data,
  index,
  badgeLabel
}: ReasonPointerWrapperProps) {
  const categoryName = data.pair.exitingFund.categoryName
  const targetFundCount = data.pair.overDiversification?.targetFundCount ?? 0

  return (
    <ReasonPointer
      index={index}
      badgeLabel={badgeLabel}
      title={`Helps reduce over-diversification in ${categoryName}`}
      highlightTitle={`${targetFundCount} funds`}
      highlightDescription={`Recommended in ${categoryName} as per allocation strategy`}
      tone="positive"
    />
  )
}

export function ReduceOverExposurePointer({
  data,
  index,
  badgeLabel
}: ReasonPointerWrapperProps) {
  return (
    <ReasonPointer
      index={index}
      badgeLabel={badgeLabel}
      title="Helps reduce high exposure"
      subtitle={`in ${data.pair.exitingFund.fundName}`}
      highlightContent={(
        <ReasonPointerHighlightStrip
          highlightTitle={`< ${planPercentageLabel({ rawPercentage: data.pair.highExposure?.targetPercentage ?? 0 })}`}
          highlightDescriptionBold="Recommended exposure in a single fund"
          highlightDescription="Spreading exposure helps reduce risk tied to a single fund."
          tone="positive"
        />
      )}
    />
  )
}

type ReasonPointerProps = {
  index?: number
  badgeLabel?: string | number
  title: string
  subtitle?: string
  highlightTitle?: string
  highlightDescription?: string
  highlightContent?: ReactNode
  tone?: ReasonPointerTone
}

export function ReasonPointer({
  index = 1,
  badgeLabel,
  title,
  subtitle,
  highlightTitle,
  highlightDescription,
  highlightContent,
  tone = 'positive'
}: ReasonPointerProps) {
  return (
    <div>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 flex items-center justify-center">
          <ReasonPointerBadge index={index} badgeLabel={badgeLabel} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div className="pt-[5px]">
            <div className="text-[14px] font-[500] leading-[140%] text-[#232323]">{title}</div>
            {subtitle ? (
              <div className="text-[14px] font-[400] leading-[140%] text-[#232323]">{subtitle}</div>
            ) : null}
          </div>
          {highlightContent ?? (
            <ReasonPointerHighlightStrip
              highlightTitle={highlightTitle ?? ''}
              highlightDescription={highlightDescription ?? ''}
              tone={tone}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export type ReasonPointerTone = 'positive' | 'warning'

function resolveReasonPointerStripTag(tone: ReasonPointerTone): EliteScoreTrends {
  if (tone === 'warning') {
    return EliteScoreTrends.AVERAGE
  }
  return EliteScoreTrends.GOOD
}

type ReasonPointerHighlightStripProps = {
  highlightTitle: string
  highlightDescription?: string
  highlightDescriptionBold?: string
  highlightDescriptionContent?: ReactNode
  tone?: ReasonPointerTone
  onInfoClick?: () => void
  analyticsScreen?: string
  fundName?: string
  fundIsin?: string
}

export function ReasonPointerHighlightStrip({
  highlightTitle,
  highlightDescription,
  highlightDescriptionBold,
  highlightDescriptionContent,
  tone = 'positive',
  onInfoClick,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation,
  fundName = '',
  fundIsin = ''
}: ReasonPointerHighlightStripProps) {
  const tag = resolveReasonPointerStripTag(tone)

  return (
    <PlanRecommendationReasonGradientStrip tag={tag}>
      <div className="flex flex-col gap-[2px]">
        <div className="flex items-center gap-1">
          <div className="text-[18px] font-[500] leading-[150%]">{highlightTitle}</div>
          {onInfoClick ? (
            <div
              className="cursor-pointer shrink-0"
              onClick={() => {
                lightImpactVibrate()
                trackEvent({
                  eventName: EventNames.UserClicked,
                  payload: {
                    ScreenName: analyticsScreen,
                    eventId: PlansLumpsumSheetEventIds.ReasonInfo,
                    buttonLabel: 'Reason info',
                    ...(fundName ? { fundName } : {}),
                    ...(fundIsin ? { fundIsin } : {})
                  }
                })
                onInfoClick()
              }}
            >
              <ReasonPointerMissedGainsInfoIcon />
            </div>
          ) : null}
        </div>
        {highlightDescriptionContent ? (
          <div className="text-[13px] leading-[140%]">{highlightDescriptionContent}</div>
        ) : null}
        {!highlightDescriptionContent && highlightDescriptionBold ? (
          <div className="text-[13px] font-[500] leading-[140%]">{highlightDescriptionBold}</div>
        ) : null}
        {!highlightDescriptionContent && highlightDescription ? (
          <div className="whitespace-pre-line text-[13px] font-[400] leading-[140%]">{highlightDescription}</div>
        ) : null}
      </div>
    </PlanRecommendationReasonGradientStrip>
  )
}

type ReasonPointerRankHighlightStripProps = {
  powerRank: number | null
  powerRating?: string | null
  description: string
  /** When set, strip/leaf color follows tone instead of powerRating (sell=warning, buy=positive). */
  tone?: ReasonPointerTone
}

export function ReasonPointerRankHighlightStrip({
  powerRank,
  powerRating,
  description,
  tone
}: ReasonPointerRankHighlightStripProps) {
  const tag = tone
    ? resolveReasonPointerStripTag(tone)
    : resolveRankStripTagFromPowerRating(powerRating)

  return (
    <PlanRecommendationReasonGradientStrip tag={tag}>
      <div className="flex flex-col gap-2">
        <ReasonPointerLeafRank powerRank={powerRank} tag={tag} />
        <div className="text-[13px] font-[400] leading-[140%]">{description}</div>
      </div>
    </PlanRecommendationReasonGradientStrip>
  )
}

type ReasonPointerLeafRankProps = {
  powerRank: number | null
  tag: EliteScoreTrends
}

export function ReasonPointerLeafRank({
  powerRank,
  tag
}: ReasonPointerLeafRankProps) {
  const rankColor = getScoreConfig(tag).textColor

  if (!powerRank) {
    return null
  }

  return (
    <div className="flex items-center gap-[2px]">
      <div className="mr-[-6px]">
        <ReasonPointerLeftLeaf fill={rankColor} fillOpacity={0.2} size={20} />
      </div>
      <div
        className="mx-[3px] min-w-2 text-center text-[18px] font-[500] leading-[150%]"
        style={{ color: rankColor }}
      >
        {powerRank}
      </div>
      <div className="ml-[-6px]">
        <ReasonPointerRightLeaf fill={rankColor} fillOpacity={0.2} size={20} />
      </div>
    </div>
  )
}

function ReasonPointerLeftLeaf({
  fill,
  fillOpacity = 0.7,
  size = 20
}: {
  fill: string
  fillOpacity?: number
  size?: number
}) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.14032 1.48699C8.40415 1.06588 8.27666 0.510623 7.85555 0.24678C7.43444 -0.0170481 6.87919 0.110446 6.61534 0.531555L6.23044 1.14592C5.9666 1.56703 6.09409 2.12228 6.51522 2.38611C6.93633 2.64996 7.49158 2.52246 7.75541 2.10135L8.14032 1.48699ZM3.3473 2.54744C3.70809 2.20149 4.27789 2.20996 4.61997 2.56634L5.47994 3.46223L6.9257 3.06179C7.40624 2.9287 7.89923 3.20872 8.02682 3.68724C8.15443 4.16575 7.86833 4.66156 7.3878 4.79466L5.42548 5.33815C5.10706 5.42636 4.76788 5.33428 4.54118 5.09812L3.31347 3.81913C2.97138 3.46275 2.98653 2.8934 3.3473 2.54744ZM3.21489 5.61269C2.8389 5.29227 2.26959 5.33949 1.9433 5.71816C1.61702 6.09681 1.65731 6.66353 2.0333 6.98395L3.76817 8.46237C4.03288 8.68795 4.40675 8.73855 4.72674 8.5921L6.82388 7.6323C7.27837 7.42429 7.48216 6.89062 7.27905 6.4403C7.07593 5.98999 6.54281 5.79357 6.08831 6.00158L4.51577 6.72129L3.21489 5.61269ZM6.82516 10.7148C7.25512 10.4641 7.40142 9.91323 7.15191 9.48439C6.90241 9.05553 6.35159 8.91112 5.92161 9.16182L4.52441 9.97647L2.79636 9.27914C2.33592 9.09334 1.81073 9.31616 1.6233 9.77685C1.43588 10.2375 1.65719 10.7616 2.11763 10.9474L4.25478 11.8099C4.51282 11.914 4.80495 11.8927 5.04592 11.7522L6.82516 10.7148Z"
        fill={fill}
        fillOpacity={fillOpacity}
      />
      <path
        d="M9.07423 11.6064L8.45068 12.7998C9.09668 12.8512 10.7827 13.2358 12.3587 14.3626C12.7476 14.6718 12.8121 15.2379 12.5028 15.6268C12.1935 16.0157 11.6274 16.0802 11.2385 15.771C10.9783 15.489 9.98655 14.8327 8.10177 14.4629C7.79071 14.422 7.50293 14.452 7.22555 14.4629L7.1136 14.452C7.1009 14.453 7.08815 14.4537 7.07532 14.4541L4.11436 14.5656C3.61267 14.5844 3.18082 14.2019 3.14982 13.7111C3.11879 13.2202 3.50032 12.8071 4.00201 12.7882L6.44175 12.6964L7.44905 10.7685C7.67542 10.3353 8.22273 10.1716 8.67151 10.403C9.12029 10.6343 9.30059 11.1731 9.07423 11.6064Z"
        fill={fill}
        fillOpacity={fillOpacity}
      />
    </svg>
  )
}

function ReasonPointerRightLeaf({
  fill,
  fillOpacity = 0.7,
  size = 20
}: {
  fill: string
  fillOpacity?: number
  size?: number
}) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.85968 1.48699C7.59585 1.06588 7.72334 0.510623 8.14445 0.24678C8.56556 -0.0170481 9.12081 0.110446 9.38466 0.531555L9.76956 1.14592C10.0334 1.56703 9.90591 2.12228 9.48478 2.38611C9.06367 2.64996 8.50842 2.52246 8.24459 2.10135L7.85968 1.48699ZM12.6527 2.54744C12.2919 2.20149 11.7221 2.20996 11.38 2.56634L10.5201 3.46223L9.0743 3.06179C8.59376 2.9287 8.10077 3.20872 7.97318 3.68724C7.84557 4.16575 8.13167 4.66156 8.6122 4.79466L10.5745 5.33815C10.8929 5.42636 11.2321 5.33428 11.4588 5.09812L12.6865 3.81913C13.0286 3.46275 13.0135 2.8934 12.6527 2.54744ZM12.7851 5.61269C13.1611 5.29227 13.7304 5.33949 14.0567 5.71816C14.383 6.09681 14.3427 6.66353 13.9667 6.98395L12.2318 8.46237C11.9671 8.68795 11.5933 8.73855 11.2733 8.5921L9.17612 7.6323C8.72163 7.42429 8.51784 6.89062 8.72095 6.4403C8.92407 5.98999 9.45719 5.79357 9.91169 6.00158L11.4842 6.72129L12.7851 5.61269ZM9.17484 10.7148C8.74488 10.4641 8.59858 9.91323 8.84809 9.48439C9.09759 9.05553 9.64841 8.91112 10.0784 9.16182L11.4756 9.97647L13.2036 9.27914C13.6641 9.09334 14.1893 9.31616 14.3767 9.77685C14.5641 10.2375 14.3428 10.7616 13.8824 10.9474L11.7452 11.8099C11.4872 11.914 11.195 11.8927 10.9541 11.7522L9.17484 10.7148Z"
        fill={fill}
        fillOpacity={fillOpacity}
      />
      <path
        d="M6.92577 11.6064L7.54932 12.7998C6.90332 12.8512 5.21732 13.2358 3.64128 14.3626C3.25243 14.6718 3.18793 15.2379 3.49717 15.6268C3.80655 16.0157 4.37261 16.0802 4.76149 15.771C5.02174 15.489 6.01345 14.8327 7.89823 14.4629C8.20929 14.422 8.49707 14.452 8.77445 14.4629L8.8864 14.452C8.8991 14.453 8.91185 14.4537 8.92468 14.4541L11.8856 14.5656C12.3873 14.5844 12.8192 14.2019 12.8502 13.7111C12.8812 13.2202 12.4997 12.8071 11.998 12.7882L9.55825 12.6964L8.55095 10.7685C8.32458 10.3353 7.77727 10.1716 7.32849 10.403C6.87971 10.6343 6.69941 11.1731 6.92577 11.6064Z"
        fill={fill}
        fillOpacity={fillOpacity}
      />
    </svg>
  )
}

export function ReasonPointerMissedGainsInfoIcon() {
  const clipPathId = useId()

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <g clipPath={`url(#${clipPathId})`}>
        <path
          d="M8 0.5C10.5717 0.5 12.4232 1.1537 13.6348 2.36523C14.8463 3.57678 15.5 5.42834 15.5 8C15.5 10.5717 14.8463 12.4232 13.6348 13.6348C12.4232 14.8463 10.5717 15.5 8 15.5C5.42834 15.5 3.57678 14.8463 2.36523 13.6348C1.1537 12.4232 0.5 10.5717 0.5 8C0.5 5.42834 1.15369 3.57678 2.36523 2.36523C3.57678 1.15369 5.42834 0.5 8 0.5Z"
          fill="white"
          stroke="#FFE2BF"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.37667 10.2069C6.00306 10.2069 5.7002 10.5023 5.7002 10.8667C5.7002 11.2312 6.00306 11.5266 6.37667 11.5266H9.62372C9.99733 11.5266 10.3002 11.2312 10.3002 10.8667C10.3002 10.5023 9.99733 10.2069 9.62372 10.2069H8.67667V7.17164C8.67667 6.80722 8.3738 6.51181 8.0002 6.51181H6.91784C6.54424 6.51181 6.24137 6.80722 6.24137 7.17164C6.24137 7.53606 6.54424 7.83148 6.91784 7.83148H7.32372V10.2069H6.37667ZM9.08255 4.5323C9.08255 5.11536 8.59796 5.58804 8.0002 5.58804C7.40243 5.58804 6.91784 5.11536 6.91784 4.5323C6.91784 3.94924 7.40243 3.47656 8.0002 3.47656C8.59796 3.47656 9.08255 3.94924 9.08255 4.5323Z"
          fill="#CC5C2D"
        />
      </g>
      <defs>
        <clipPath id={clipPathId}>
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}

export function resolveReasonPointerBadgeLabel({
  totalPointerCount,
  showsPercentageHighlight
}: {
  totalPointerCount: number
  showsPercentageHighlight: boolean
}): '%' | undefined {
  if (totalPointerCount === 1 && showsPercentageHighlight) {
    return '%'
  }

  return undefined
}

export function PercentageIconIfOnlyOneReason() {
  return <RenderHTML innerHTMLText={`<svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M0.75 16.75C0.75 20.9935 2.43571 25.0631 5.43629 28.0637C8.43687 31.0643 12.5065 32.75 16.75 32.75C20.9935 32.75 25.0631 31.0643 28.0637 28.0637C31.0643 25.0631 32.75 20.9935 32.75 16.75C32.75 12.5065 31.0643 8.43687 28.0637 5.43629C25.0631 2.43571 20.9935 0.75 16.75 0.75C12.5065 0.75 8.43687 2.43571 5.43629 5.43629C2.43571 8.43687 0.75 12.5065 0.75 16.75Z" stroke="#D5DADB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M13.4858 15.8192C14.7724 15.8192 15.8153 14.7763 15.8153 13.4897C15.8153 12.2031 14.7724 11.1602 13.4858 11.1602C12.1992 11.1602 11.1562 12.2031 11.1562 13.4897C11.1562 14.7763 12.1992 15.8192 13.4858 15.8192Z" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M20.0092 22.3427C21.2958 22.3427 22.3388 21.2997 22.3388 20.0131C22.3388 18.7266 21.2958 17.6836 20.0092 17.6836C18.7227 17.6836 17.6797 18.7266 17.6797 20.0131C17.6797 21.2997 18.7227 22.3427 20.0092 22.3427Z" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M11.1562 22.342L22.3381 11.1602" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`} />
}

export function ReasonPointerBadge({
  index,
  badgeLabel
}: {
  index: number
  badgeLabel?: string | number
}) {
  if (badgeLabel === '%') {
    return <PercentageIconIfOnlyOneReason />
  }
  const displayIndex = typeof badgeLabel === 'number' ? badgeLabel : index
  if (displayIndex < 1) {
    return null
  }
  return <NumberIcon number={displayIndex} />
}

export function ReasonPointerCategoryExposureDescription({
  categoryName,
  actionDescription
}: {
  categoryName: string
  actionDescription: string
}) {
  return (
    <>
      <span className="font-[500]">{`in ${categoryName}`}</span>
      <span className="font-[400]">{` ${actionDescription}`}</span>
    </>
  )
}
